import ExcelJS from 'exceljs'
import { atBuffer2DIndex, GeckoBinnedDataBuffer } from 'features/dataLayers'
import { forecastMath } from 'features/math'
import {
  GeckoComponentDataFrame,
  GeckoComponentModel,
  GeckoComponentModelType,
  GeckoInspection,
  GeckoLegacyBoilerWall,
  GeckoMeasurement,
  GeckoMeasurementUnit,
  GeckoShellCourse,
} from 'types'

const { max, min } = forecastMath

const FORMULAS = ['MIN', 'MAX', 'AVERAGE']
const CUSTOMER_ACK =
  'By utilizing this report, the Customer acknowledges responsibility for and agrees that the accuracy and interpretation of inspection data within Cantilever and the accuracy and interpretation of the several outputs of Cantilever, will be dependent on the accuracy and use of Customer-controlled data variables, logic rules, system functions, and its own independent analysis.'

export const createSummarySheet = (
  workbook: ExcelJS.Workbook,
  orgName: string,
  siteName: string,
  unitName: string,
  inspectionDate: string,
  inspectionMethod: string | undefined,
  thicknessUnits: string | undefined,
  xMeasurementUnits: string,
  yMeasurementUnits: string | undefined
) => {
  const summarySheet = workbook.addWorksheet('Summary')
  const data = [
    ['Organization', orgName],
    ['Site', siteName],
    ['Unit', unitName],
    ['Date', inspectionDate],
    ['Generated on', new Date().toISOString().split('T')[0]],
    ['Inspection', inspectionMethod === 'None' ? 'N/A' : inspectionMethod],
    ['Thickness Units', thicknessUnits === 'None' ? 'N/A' : thicknessUnits],
    [
      'Position Units',
      xMeasurementUnits === 'None' ? 'N/A' : xMeasurementUnits,
    ],
    [
      'Elevation Units',
      yMeasurementUnits === 'None' ? 'N/A' : yMeasurementUnits,
    ],
  ]

  const col1_width = 15
  let col2width = 15

  data.forEach((row) => {
    summarySheet.addRow(row)
    if (typeof row[1] === 'string') {
      col2width = Math.max(col2width, row[1].length)
    }
    summarySheet.getColumn(1).width = col1_width
    summarySheet.getColumn(2).width = col2width
  })
  const customerAckRow = summarySheet.addRow([CUSTOMER_ACK])
  customerAckRow.getCell(1).alignment = {
    horizontal: 'left',
    wrapText: true,
  }
}

const getGeckoMeasurement = (
  value: number,
  binSize: number,
  origin: number
) => {
  return binSize * value + origin + binSize / 2
}

export const convertToCustomerX = (
  value: number,
  xEnd: number,
  customerXEnd: number | undefined,
  customerXInverted: boolean
) => {
  if (
    (customerXEnd === undefined || xEnd === customerXEnd) &&
    !customerXInverted
  ) {
    return value
  }
  const distFromXEnd = xEnd - value
  customerXEnd = customerXEnd === undefined ? xEnd : customerXEnd
  const customerValue = !customerXInverted
    ? customerXEnd - distFromXEnd
    : customerXEnd + distFromXEnd
  return customerValue
}

export const convertToRespectiveSectionCustomerX = (
  value: number,
  sections: GeckoLegacyBoilerWall[],
  binWidth: number,
  maxBin: number
) => {
  const start = Number.NEGATIVE_INFINITY
  for (const section of sections) {
    let xEnd = section.geometry.x.end.num
    if (!xEnd) {
      xEnd = maxBin
    }
    const inclusiveXEnd = xEnd !== undefined ? xEnd - binWidth / 2 : null
    if (inclusiveXEnd && value >= start && value <= inclusiveXEnd) {
      const customerXInverted = section.customerXInverted || false
      const customerXEnd = section.customerXEnd?.num
      return convertToCustomerX(
        value,
        xEnd - 0.5,
        customerXEnd,
        customerXInverted
      )
    }
  }
}

export const convertToBinIndex = (
  origin: number,
  binSize: number,
  valIfNone: number,
  rawValue?: number,
  roundingMethod?: 'ceil' | 'floor' | 'round'
) => {
  if (rawValue === undefined) {
    return valIfNone
  }
  const index = (rawValue - origin) / binSize
  return !roundingMethod || roundingMethod === 'round'
    ? Math.round(index)
    : roundingMethod === 'ceil'
      ? Math.ceil(index)
      : Math.floor(index)
}

const setValueAndAlign = (
  dataSheet: ExcelJS.Worksheet,
  row: number,
  column: number,
  value: number | string,
  isFormula?: boolean
) => {
  const cell = dataSheet.getCell(row, column)
  if (isFormula) cell.value = { formula: value as string }
  else cell.value = value
  cell.alignment = { horizontal: 'center' }
}

const convertBinToCorrectUnits = (
  preferredUnits: GeckoMeasurementUnit,
  currentUnits: GeckoMeasurementUnit,
  bin: number
) => {
  const convertedValue = GeckoMeasurement.CreateFromValue(
    bin,
    currentUnits
  ).convertTo(preferredUnits).num

  return parseFloat(convertedValue.toFixed(3))
}

const getColumnLetter = (column: number) => {
  let columnLetter = ''
  let tempColumn = column
  while (tempColumn > 0) {
    tempColumn -= 1
    const remainder = tempColumn % 26
    columnLetter = String.fromCharCode(65 + remainder) + columnLetter
    tempColumn = Math.floor(tempColumn / 26)
  }
  return columnLetter
}

export const getCellString = (row: number, column: number) => {
  if (row <= 0 || column <= 0) {
    throw new Error('Row and column must be positive integers')
  }
  const columnLetter = getColumnLetter(column)
  return `${columnLetter}${row}`
}

const addFormulas = (
  dataSheet: ExcelJS.Worksheet,
  xCol: number,
  minYRow: number,
  maxYRow: number
) => {
  const startCell = getCellString(minYRow, xCol)
  const endCell = getCellString(maxYRow, xCol)
  const formulaRange = `${startCell}:${endCell}`

  const firstFormulaRow = 2
  for (let i = 0; i < FORMULAS.length; i++) {
    const formula = FORMULAS[i]
    const eqn = `=IF(COUNT(${formulaRange}) > 0, ROUND(${formula}(${formulaRange}), 3), "")`
    setValueAndAlign(dataSheet, firstFormulaRow + i, xCol, eqn, true)
  }
}

export const createDataSheet = (
  workbook: ExcelJS.Workbook,
  title: string,
  binnedDataBuffer: GeckoBinnedDataBuffer,
  selectedCDF: GeckoComponentDataFrame,
  thicknessUnits: GeckoMeasurementUnit,
  xAxisUnits: GeckoMeasurementUnit,
  yAxisUnits: GeckoMeasurementUnit,
  getPreferredMeasurementUnit: (
    inputUnit: GeckoMeasurementUnit
  ) => GeckoMeasurementUnit,
  xRange?: [number?, number?],
  yRange?: [number?, number?],
  rangeRoundingMethod?: 'ceil' | 'floor' | 'round',
  convertToRespectiveCustomerX?: (x: number) => number | undefined
) => {
  const binWidth = selectedCDF.data.binning.bin_size.width.toNumber(xAxisUnits)
  const binHeight =
    selectedCDF.data.binning.bin_size.height.toNumber(yAxisUnits)
  const originX = selectedCDF.data.geometry.x.start.toNumber(xAxisUnits)
  const originY = selectedCDF.data.geometry.y.start.toNumber(yAxisUnits)
  const cscanHeight = selectedCDF.data.binning.y_bin_count
  const cscanWidth = selectedCDF.data.binning.x_bin_count
  let xStart = convertToBinIndex(
    originX,
    binWidth,
    0,
    xRange !== undefined ? xRange[0] : undefined,
    rangeRoundingMethod
  )
  let xEnd = convertToBinIndex(
    originX,
    binWidth,
    cscanWidth,
    xRange !== undefined ? xRange[1] : undefined,
    rangeRoundingMethod
  )
  let yStart = convertToBinIndex(
    originY,
    binHeight,
    0,
    yRange !== undefined ? yRange[0] : undefined,
    rangeRoundingMethod
  )
  let yEnd = convertToBinIndex(
    originY,
    binHeight,
    cscanHeight,
    yRange !== undefined ? yRange[1] : undefined,
    rangeRoundingMethod
  )
  xStart = max(xStart, 0)
  yStart = max(yStart, 0)
  xEnd = min(xEnd, cscanWidth)
  yEnd = min(yEnd, cscanHeight)

  // Extend the range at boundary if rounding method is 'floor' or 'ceil'
  if (rangeRoundingMethod === 'floor') {
    if (xEnd === cscanWidth - 1) xEnd = cscanWidth
    if (yEnd === cscanHeight - 1) yEnd = cscanHeight
  } else if (rangeRoundingMethod === 'ceil') {
    if (xStart === 1) xStart = 0
    if (yStart === 1) yStart = 0
  }

  if (yStart >= yEnd || xStart >= xEnd) {
    return
  }
  const dataSheet = workbook.addWorksheet(title)

  setValueAndAlign(dataSheet, 2, 1, 'MIN')
  setValueAndAlign(dataSheet, 3, 1, 'MAX')
  setValueAndAlign(dataSheet, 4, 1, 'AVERAGE')

  const firstYRow = 5
  const firstXCol = 1

  const minYRow = firstYRow
  const maxYRow = yEnd - 1 - yStart + firstYRow
  for (let x = xStart; x < xEnd; x++) {
    const xCol = x + 1 - xStart + firstXCol
    const geckoXBin = getGeckoMeasurement(x, binWidth, originX)
    const customerXBin =
      convertToRespectiveCustomerX === undefined
        ? geckoXBin
        : (convertToRespectiveCustomerX(geckoXBin) ?? geckoXBin)
    const convertedXBin = convertBinToCorrectUnits(
      getPreferredMeasurementUnit(xAxisUnits),
      xAxisUnits,
      customerXBin
    )

    setValueAndAlign(dataSheet, 1, xCol, convertedXBin)
    addFormulas(dataSheet, xCol, minYRow, maxYRow)
    // populate data for column x
    for (let y = yStart; y < yEnd; y++) {
      const cdfThicknessVal = atBuffer2DIndex(binnedDataBuffer, x, y)
      if (Number.isNaN(cdfThicknessVal) || cdfThicknessVal === undefined) {
        continue
      }
      const yCell = yEnd - (y + 1) + firstYRow
      const convertedValue = convertBinToCorrectUnits(
        getPreferredMeasurementUnit(thicknessUnits),
        thicknessUnits,
        cdfThicknessVal
      )
      setValueAndAlign(
        dataSheet,
        yCell,
        xCol,
        convertedValue ? convertedValue : cdfThicknessVal
      )
    }
  }
  // set y axis
  for (let y = yStart; y < yEnd; y++) {
    const yCell = yEnd - (y + 1) + firstYRow
    const yBin = getGeckoMeasurement(y, binHeight, originY)
    const convertedYBin = convertBinToCorrectUnits(
      getPreferredMeasurementUnit(yAxisUnits),
      yAxisUnits,
      yBin
    )

    setValueAndAlign(dataSheet, yCell, 1, convertedYBin)
  }
  // freeze panes at B5
  dataSheet.views = [{ state: 'frozen', xSplit: 1, ySplit: 4 }]
}

export const createWaterwallSheets = (
  workbook: ExcelJS.Workbook,
  sections: GeckoLegacyBoilerWall[],
  thicknessUnits: GeckoMeasurementUnit,
  xAxisUnits: GeckoMeasurementUnit,
  yAxisUnits: GeckoMeasurementUnit,
  getPreferredMeasurementUnit: (
    inputUnit: GeckoMeasurementUnit
  ) => GeckoMeasurementUnit,
  binnedDataBuffer: GeckoBinnedDataBuffer,
  selectedCDF: GeckoComponentDataFrame
) => {
  let start: number | undefined = undefined
  const originX = selectedCDF.data.geometry.x.start.num
  const binWidth = selectedCDF.data.binning.bin_size.width.toNumber(xAxisUnits)
  const maxXEnd = getGeckoMeasurement(
    selectedCDF.data.binning.x_bin_count,
    binWidth,
    originX
  )
  sections.forEach((section) => {
    const end = section.geometry.x.end.toNumber(xAxisUnits)
    const finiteXEnd = (end ? end : maxXEnd) as number
    const inclusiveXEnd = finiteXEnd - binWidth / 2
    const xRange: [(number | undefined)?, (number | undefined)?] = [
      start,
      inclusiveXEnd,
    ]
    const customerXInverted =
      section.customerXInverted !== undefined
        ? section.customerXInverted
        : false
    const customerXEnd = section.customerXEnd?.toNumber(xAxisUnits)
    createDataSheet(
      workbook,
      section.name,
      binnedDataBuffer,
      selectedCDF,
      thicknessUnits,
      xAxisUnits,
      yAxisUnits,
      getPreferredMeasurementUnit,
      xRange,
      [undefined, undefined],
      undefined,
      (value: number) =>
        convertToCustomerX(
          value,
          finiteXEnd - 0.5,
          customerXEnd,
          customerXInverted
        )
    )
    start = end
  })
}

export const createShellCourseSheets = (
  workbook: ExcelJS.Workbook,
  sections: GeckoShellCourse[],
  thicknessUnits: GeckoMeasurementUnit,
  xAxisUnits: GeckoMeasurementUnit,
  yAxisUnits: GeckoMeasurementUnit,
  getPreferredMeasurementUnit: (
    inputUnit: GeckoMeasurementUnit
  ) => GeckoMeasurementUnit,
  binnedDataBuffer: GeckoBinnedDataBuffer,
  selectedCDF: GeckoComponentDataFrame
) => {
  let start: number
  sections.forEach((section) => {
    const end = section.geometry.y.end.toNumber(yAxisUnits)
    const yRange: [(number | undefined)?, (number | undefined)?] = [start, end]
    createDataSheet(
      workbook,
      section.name,
      binnedDataBuffer,
      selectedCDF,
      thicknessUnits,
      xAxisUnits,
      yAxisUnits,
      getPreferredMeasurementUnit,
      [undefined, undefined],
      yRange,
      'floor'
    )
    start = end
  })
}

export const createExcelSheet = async (
  orgName: string,
  siteName: string,
  unitName: string,
  componentName: string,
  thicknessUnits: GeckoMeasurementUnit,
  xAxisUnits: GeckoMeasurementUnit,
  yAxisUnits: GeckoMeasurementUnit,
  getPreferredMeasurementUnit: (
    inputUnit: GeckoMeasurementUnit
  ) => GeckoMeasurementUnit,
  selectedComponentModel: GeckoComponentModel | undefined,
  binnedDataBuffer: GeckoBinnedDataBuffer,
  selectedCDF: GeckoComponentDataFrame,
  selectedInspection: GeckoInspection
): Promise<ExcelJS.Workbook> => {
  // ---------- POPULATE EXCEL SHEET ----------
  const workbook = new ExcelJS.Workbook()

  const binWidth = selectedCDF.data.binning.bin_size.width.toNumber(xAxisUnits)

  const originX = selectedCDF.data.geometry.x.start.toNumber(xAxisUnits)

  // ---------- CREATE SUMMARY SHEET ----------
  createSummarySheet(
    workbook,
    orgName,
    siteName,
    unitName,
    selectedInspection.data.date?.toISOString().split('T')[0] || 'N/A',
    selectedCDF.data.params.source.name || 'N/A',
    getPreferredMeasurementUnit(thicknessUnits).name || 'N/A',
    getPreferredMeasurementUnit(xAxisUnits).name || 'N/A',
    getPreferredMeasurementUnit(yAxisUnits).name || 'N/A'
  )

  if (
    selectedComponentModel !== undefined &&
    selectedComponentModel.type === GeckoComponentModelType.LegacyBoilerWalls &&
    selectedComponentModel.wallsArray.length > 0
  ) {
    const walls = selectedComponentModel.wallsArray
    const maxBin = getGeckoMeasurement(
      selectedCDF.data.binning.x_bin_count,
      binWidth,
      originX
    )

    // ------ CREATE DATA SHEET ------
    createDataSheet(
      workbook,
      componentName,
      binnedDataBuffer,
      selectedCDF,
      thicknessUnits,
      xAxisUnits,
      yAxisUnits,
      getPreferredMeasurementUnit,
      undefined,
      undefined,
      undefined,
      (x: number) =>
        convertToRespectiveSectionCustomerX(x, walls, binWidth, maxBin)
    )
    if (selectedComponentModel.wallsArray.length > 1) {
      // ------ CREATE WATERWALLS SECTIONS SHEET ------
      createWaterwallSheets(
        workbook,
        walls,
        thicknessUnits,
        xAxisUnits,
        yAxisUnits,
        getPreferredMeasurementUnit,
        binnedDataBuffer,
        selectedCDF
      )
    }
  } else {
    // ------ CREATE DATA SHEET ------
    createDataSheet(
      workbook,
      componentName,
      binnedDataBuffer,
      selectedCDF,
      thicknessUnits,
      xAxisUnits,
      yAxisUnits,
      getPreferredMeasurementUnit
    )
    if (
      selectedComponentModel !== undefined &&
      selectedComponentModel.type === GeckoComponentModelType.VerticalTankShell
    ) {
      const courses = selectedComponentModel.coursesArray
      if (courses.length > 1) {
        // ------ CREATE SHELL COURSE SECTIONS SHEET ------
        createShellCourseSheets(
          workbook,
          courses,
          thicknessUnits,
          xAxisUnits,
          yAxisUnits,
          getPreferredMeasurementUnit,
          binnedDataBuffer,
          selectedCDF
        )
      }
    }
  }

  return workbook
}
